import React, { useEffect, useState } from 'react';
import {
  Input,
  Select,
  HStack,
  Button,
  Avatar,
  Stack,
  FormControl,
  FormLabel,
  Textarea,
  Box,
  VStack,
  useToast,
  Text,
  Grid,
  Heading,
  Divider,
  Icon,
  Circle,
  useColorModeValue,
  InputGroup,
  InputRightElement
} from '@chakra-ui/react';
import axios from 'axios';
import { getUser } from '../../cookie/user';
import useStore from '../../store/store';
import { useDropzone } from 'react-dropzone';
import { 
  FiCamera, 
  FiUser, 
  FiMail, 
  FiPhone, 
  FiMapPin, 
  FiCalendar, 
  FiLock,
  FiEye,
  FiEyeOff
} from 'react-icons/fi';
import { motion } from 'framer-motion';

const Profile = () => {
  const user = getUser();
  const { setUser } = useStore();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [passLoading, setPassLoading] = useState(false);
  const [fileUrl, setFileUrl] = useState(user?.fileUrl || '');

  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    dateOfBirth: user?.dateOfBirth || '',
    phone: user?.phone || '',
    country: user?.country || '',
    state: user?.state || '',
    city: user?.city || '',
    gender: user?.gender || '',
    pincode: user?.pincode || '',
    email: user?.email || '',
    address: user?.address || '',
    fileUrl: user?.fileUrl || '',
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPass, setShowPass] = useState({ old: false, new: false, confirm: false });

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
    multiple: false,
    onDrop: async accepted => {
      const file = accepted[0];
      setFileUrl(URL.createObjectURL(file));
      try {
        setLoading(true);
        const data = new FormData();
        data.append('file', file);
        data.append('upload_preset', 'vv0rqrrj');
        data.append('cloud_name', 'ddqlrpysr');

        const response = await axios.post(
          'https://api.cloudinary.com/v1_1/ddqlrpysr/image/upload',
          data
        );

        if (response.status === 200) {
          const imageUrl = response.data.secure_url;
          setFileUrl(imageUrl);
          setProfileData(prev => ({ ...prev, fileUrl: imageUrl }));
          toast({ title: 'Photo uploaded!', status: 'info', duration: 2000 });
        }
      } catch (error) {
        toast({ title: 'Upload failed', status: 'error' });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleFormChange = event => {
    setProfileData({
      ...profileData,
      [event.target.name]: event.target.value,
    });
  };

  const handlePasswordChange = event => {
    setPasswordData({
      ...passwordData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async event => {
    event.preventDefault();
    try {
      setLoading(true);
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER}/user/${user?._id}`,
        profileData
      );

      if (response.status === 200) {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast({
          title: 'Profile Updated Successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({ title: 'Update Failed', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({ title: "Passwords don't match", status: 'error' });
      return;
    }
    if (!passwordData.oldPassword || !passwordData.newPassword) {
      toast({ title: "Please fill all fields", status: 'error' });
      return;
    }

    try {
      setPassLoading(true);
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER}/update-password/${user?._id}`,
        {
          oldPassword: passwordData.oldPassword,
          newPassword: passwordData.newPassword
        }
      );

      if (response.data.success) {
        toast({ title: 'Password updated!', status: 'success' });
        setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      toast({ 
        title: error.response?.data?.message || 'Password update failed', 
        status: 'error' 
      });
    } finally {
      setPassLoading(false);
    }
  };

  useEffect(() => {
    if (user?.dateOfBirth) {
      const dob = new Date(user.dateOfBirth);
      if (!isNaN(dob.getTime())) {
        const formattedDOB = dob.toISOString().split('T')[0];
        setProfileData(prev => ({ ...prev, dateOfBirth: formattedDOB }));
      }
    }
  }, [user?.dateOfBirth]);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  return (
    <VStack spacing={8} width="100%" align="stretch" pb={10}>
      <Box bg={cardBg} p={8} borderRadius="2xl" border="1px solid" borderColor={borderColor} shadow="sm">
        <Stack direction={{ base: 'column', md: 'row' }} spacing={8} align="center">
          <Box position="relative">
            <Avatar size="2xl" src={fileUrl} name={`${profileData.firstName} ${profileData.lastName}`} border="4px solid" borderColor="teal.500" />
            <Circle
              {...getRootProps()}
              size="40px"
              bg="teal.500"
              color="white"
              position="absolute"
              bottom="2"
              right="2"
              cursor="pointer"
              shadow="lg"
              _hover={{ bg: 'teal.600' }}
            >
              <Input {...getInputProps()} />
              <Icon as={FiCamera} />
            </Circle>
          </Box>
          <VStack align={{ base: 'center', md: 'start' }} spacing={1}>
            <Heading size="lg" color="gray.800">
              {profileData.firstName} {profileData.lastName}
            </Heading>
            <Text color="gray.500">{profileData.email}</Text>
            <Text fontSize="sm" color="teal.600" fontWeight="bold">
              Account ID: {user?._id?.slice(-8).toUpperCase()}
            </Text>
          </VStack>
        </Stack>
      </Box>

      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={8}>
        <VStack spacing={8} align="stretch">
          <VStack bg={cardBg} p={8} borderRadius="2xl" border="1px solid" borderColor={borderColor} shadow="sm" align="stretch" spacing={6}>
            <HStack spacing={3}>
              <Icon as={FiUser} color="teal.500" fontSize="xl" />
              <Heading size="md">Personal Details</Heading>
            </HStack>
            <Divider />
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">First Name</FormLabel>
                <InputGroupLeft icon={FiUser}>
                  <Input name="firstName" value={profileData.firstName} onChange={handleFormChange} placeholder="First Name" focusBorderColor="teal.400" borderRadius="lg" />
                </InputGroupLeft>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">Last Name</FormLabel>
                <InputGroupLeft icon={FiUser}>
                  <Input name="lastName" value={profileData.lastName} onChange={handleFormChange} placeholder="Last Name" focusBorderColor="teal.400" borderRadius="lg" />
                </InputGroupLeft>
              </FormControl>
            </Grid>
            <FormControl>
               <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">Date of Birth</FormLabel>
               <InputGroupLeft icon={FiCalendar}>
                 <Input type="date" name="dateOfBirth" value={profileData.dateOfBirth} onChange={handleFormChange} focusBorderColor="teal.400" borderRadius="lg" />
               </InputGroupLeft>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">Gender</FormLabel>
              <InputGroupLeft icon={FiUser}>
                <Select name="gender" value={profileData.gender} onChange={handleFormChange} focusBorderColor="teal.400" borderRadius="lg">
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Others">Other</option>
                </Select>
              </InputGroupLeft>
            </FormControl>
          </VStack>

          <VStack bg={cardBg} p={8} borderRadius="2xl" border="1px solid" borderColor={borderColor} shadow="sm" align="stretch" spacing={6}>
            <HStack spacing={3}>
              <Icon as={FiMail} color="teal.500" fontSize="xl" />
              <Heading size="md">Contact Info</Heading>
            </HStack>
            <Divider />
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">Email Address</FormLabel>
              <InputGroupLeft icon={FiMail}>
                <Input type="email" name="email" value={profileData.email} onChange={handleFormChange} focusBorderColor="teal.400" borderRadius="lg" />
              </InputGroupLeft>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">Phone Number</FormLabel>
              <InputGroupLeft icon={FiPhone}>
                <Input type="tel" name="phone" value={profileData.phone} onChange={handleFormChange} focusBorderColor="teal.400" borderRadius="lg" />
              </InputGroupLeft>
            </FormControl>
          </VStack>
        </VStack>

        <VStack spacing={8} align="stretch">
          <VStack bg={cardBg} p={8} borderRadius="2xl" border="1px solid" borderColor={borderColor} shadow="sm" align="stretch" spacing={6}>
            <HStack spacing={3}>
              <Icon as={FiLock} color="teal.500" fontSize="xl" />
              <Heading size="md">Security</Heading>
            </HStack>
            <Divider />
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">Current Password</FormLabel>
              <InputGroup>
                <Input 
                  type={showPass.old ? 'text' : 'password'} 
                  name="oldPassword" 
                  value={passwordData.oldPassword} 
                  onChange={handlePasswordChange} 
                  placeholder="********" 
                  focusBorderColor="teal.400" 
                  borderRadius="lg" 
                />
                <InputRightElement h={'full'}>
                  <Button aria-label="Toggle password visibility" variant={'ghost'} onClick={() => setShowPass(p => ({ ...p, old: !p.old }))}>
                    {showPass.old ? <FiEye /> : <FiEyeOff />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">New Password</FormLabel>
              <InputGroup>
                <Input 
                  type={showPass.new ? 'text' : 'password'} 
                  name="newPassword" 
                  value={passwordData.newPassword} 
                  onChange={handlePasswordChange} 
                  placeholder="Min 8 characters" 
                  focusBorderColor="teal.400" 
                  borderRadius="lg" 
                />
                <InputRightElement h={'full'}>
                  <Button aria-label="Toggle password visibility" variant={'ghost'} onClick={() => setShowPass(p => ({ ...p, new: !p.new }))}>
                    {showPass.new ? <FiEye /> : <FiEyeOff />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">Confirm New Password</FormLabel>
              <InputGroup>
                <Input 
                  type={showPass.confirm ? 'text' : 'password'} 
                  name="confirmPassword" 
                  value={passwordData.confirmPassword} 
                  onChange={handlePasswordChange} 
                  placeholder="Confirm new password" 
                  focusBorderColor="teal.400" 
                  borderRadius="lg" 
                />
                <InputRightElement h={'full'}>
                  <Button aria-label="Toggle password visibility" variant={'ghost'} onClick={() => setShowPass(p => ({ ...p, confirm: !p.confirm }))}>
                    {showPass.confirm ? <FiEye /> : <FiEyeOff />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
            <Button colorScheme="teal" variant="outline" size="sm" isLoading={passLoading} onClick={handleUpdatePassword}>
              Update Password
            </Button>
          </VStack>

          <VStack bg={cardBg} p={8} borderRadius="2xl" border="1px solid" borderColor={borderColor} shadow="sm" align="stretch" spacing={6}>
            <HStack spacing={3}>
              <Icon as={FiMapPin} color="teal.500" fontSize="xl" />
              <Heading size="md">Address Details</Heading>
            </HStack>
            <Divider />
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
               <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">Pincode</FormLabel>
                  <Input name="pincode" value={profileData.pincode} onChange={handleFormChange} placeholder="400001" focusBorderColor="teal.400" borderRadius="lg" />
               </FormControl>
               <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">City</FormLabel>
                  <Input name="city" value={profileData.city} onChange={handleFormChange} placeholder="City" focusBorderColor="teal.400" borderRadius="lg" />
               </FormControl>
               <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">State</FormLabel>
                  <Input name="state" value={profileData.state} onChange={handleFormChange} placeholder="State" focusBorderColor="teal.400" borderRadius="lg" />
               </FormControl>
               <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">Country</FormLabel>
                  <Input name="country" value={profileData.country} onChange={handleFormChange} placeholder="Country" focusBorderColor="teal.400" borderRadius="lg" />
               </FormControl>
            </Grid>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">Full Address</FormLabel>
              <Textarea name="address" value={profileData.address} onChange={handleFormChange} placeholder="Full address details" focusBorderColor="teal.400" borderRadius="lg" rows={3} />
            </FormControl>
          </VStack>
        </VStack>
      </Grid>

      {JSON.stringify(profileData) !== JSON.stringify({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
        phone: user?.phone || '',
        country: user?.country || '',
        state: user?.state || '',
        city: user?.city || '',
        gender: user?.gender || '',
        pincode: user?.pincode || '',
        email: user?.email || '',
        address: user?.address || '',
        fileUrl: user?.fileUrl || '',
      }) && (
        <Box pt={4} as={motion.div} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Button
            width={{ base: 'full', md: '240px' }}
            height="55px"
            colorScheme="teal"
            borderRadius="full"
            shadow="lg"
            isLoading={loading}
            onClick={handleSubmit}
            _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
          >
            Save All Changes
          </Button>
        </Box>
      )}
    </VStack>
  );
};

const InputGroupLeft = ({ icon, children }) => (
  <HStack spacing={0} position="relative" width="full">
    <Box position="absolute" left="3" zIndex={2} color="gray.400">
      <Icon as={icon} />
    </Box>
    <Box width="full" sx={{ '& input, & select': { pl: '10' } }}>
      {children}
    </Box>
  </HStack>
);

export default Profile;
